import dayjs from 'dayjs';

import requestInstance from '../../utils/requestInstance';
import {
  START_DATE_OF_ALERTS_ROUTING_STATUS,
  STATUS,
  MAX_TRIGGERS_SHOWN,
  MAX_INCIDENT_CHARACTERS_SHOWN
} from '../../constants/alertsRoutingStatusConstant';

const state = {
  isLoadingData: false,
  isSendingData: false,
  isDeletingData: false,
  isEditingData: false,
  isSnoozingData: false,
  isUnSnoozingData: false,
  isShowCreateAlertModal: false,
  isEditingAlert: false,
  isShowSnoozeAlertModal: false,
  isShowDeleteAlertModal: false,
  isShowSuccessInfoBanner: false,
  isShowErrorInfoBanner: false,
  successInfoBannerText: '',
  errorInfoBannerText: '',
  alertNameQuery: '',
  searchQuery: '',
  prefixListAnnouncedByASNs: {},
  prefixListDelegatedToAccounts: {},
  delegatedPrefixes: {},
  memberContacts: {},
  alertsRoutingStatusSummary: {},
  firingAlertsRoutingStatusAmount: {},
  showMoreModalObj: {
    isShown: false,
    bodyTextOfTriggers: '',
    bodyTextOfIncident: '',
    bodyTextOfFilters: '',
    modalType: '',
    alertId: null
  },
  expandedAlertId: null
};

const getters = {
  selectedAccountId: (state, getters, rootState) => {
    return (
      rootState.dataStore.selectedAccount &&
      rootState.dataStore.selectedAccount.id
    );
  },

  selectedAccountName: (state, getters, rootState) => {
    return (
      rootState.dataStore.selectedAccount &&
      rootState.dataStore.selectedAccount.account
    );
  },

  prefixListAnnouncedByASNsWithSelectedAccount: (state, getters) => {
    const selectedAccountId = getters.selectedAccountId;
    if (!selectedAccountId) return [];

    const prefixList = state.prefixListAnnouncedByASNs[selectedAccountId] || [];

    return prefixList.map(prefix => {
      const isHeldByAccount = getters.delegatedPrefixListOfSelectedAccount.includes(
        prefix
      );

      return {
        prefix,
        isHeldByAccount
      };
    });
  },

  delegatedPrefixListOfSelectedAccount: (state, getters) => {
    const selectedAccountId = getters.selectedAccountId;
    if (!selectedAccountId) return [];

    const prefixList = state.delegatedPrefixes[selectedAccountId];
    return prefixList;
  },

  memberContactsOfSelectedAccount: (state, getters, rootState, y) => {
    const selectedAccountId = getters.selectedAccountId;
    if (!selectedAccountId) return [];

    let memberContacts = state.memberContacts[selectedAccountId];

    // Remove contact from memberContacts if its uid equals to the userid of current user.
    const userid =
      rootState.authStore &&
      rootState.authStore.user &&
      rootState.authStore.user.userid;
    if (userid)
      memberContacts = memberContacts.filter(item => item.uid !== userid);

    return memberContacts;
  },

  alertsSummaryOfSelectedAccount: (state, getters) => {
    const selectedAccountId = getters.selectedAccountId;
    if (!selectedAccountId) return {};

    const alertsSummary =
      state.alertsRoutingStatusSummary[selectedAccountId] || {};
    return alertsSummary;
  },

  alertsListOfSelectedAccount: (state, getters) => {
    const alertsList = getters.alertsSummaryOfSelectedAccount.alerts || null;
    return alertsList;
  },

  alertsNameListOfSelectedAccount: (state, getters) => {
    const alerts = getters.alertsSummaryOfSelectedAccount.alerts || [];
    const alertsNameList = alerts.map(item => item.name);
    return alertsNameList;
  },

  firingAlertsAmountOfSelectedAccount: (state, getters) => {
    const memberId = getters.selectedAccountId;
    return state.firingAlertsRoutingStatusAmount[memberId] || 0;
  },

  lastFiringAlertOfSelectedAccount: (state, getters) => {
    const lastFiringAlert =
      getters.alertsSummaryOfSelectedAccount.lastFiringAlertWeek || {};
    return lastFiringAlert;
  },

  topTriggerListOfSelectedAccount: (state, getters) => {
    const topTriggers =
      getters.alertsSummaryOfSelectedAccount.topTriggersWeek || [];
    return topTriggers;
  }
};

const actions = {
  // Fetch prefix list of a selected member. It is used when create /edit alert.
  fetchDelegatedPrefixesByMemberId: (context, memberId) => {
    if (context.state.delegatedPrefixes[memberId]) return;

    context.commit('setLoadingData', true);

    requestInstance
      .get(`/prefixes/${memberId}`)
      .then(res => {
        context.commit('setLoadingData', false);
        context.commit('setDelegatedPrefixes', {
          prefixes: res.data,
          memberId
        });
      })
      .catch(err => {
        console.error('fetchDelegatedPrefixesByMemberId err: ', err);
        context.dispatch('setErrorInfoBanner', '', { root: true });
        context.commit('setLoadingData', false);
      });
  },

  fetchMemberContactsByMemberAccountName: (context, payload) => {
    const { accountName, memberId } = payload;

    if (context.state.memberContacts[memberId]) return;

    context.commit('setLoadingData', true);

    requestInstance
      .get(`/alerts/routing-status/contacts/${accountName}`)
      .then(res => {
        context.commit('setLoadingData', false);
        context.commit('setMemberContacts', {
          memberId,
          memberContacts: res.data
        });
      })
      .catch(err => {
        console.error('fetchMemberContactsByMemberAccountName err: ', err);
        context.dispatch('setErrorInfoBanner', '', { root: true });
        context.commit('setLoadingData', false);
      });
  },

  fetchAlertsRoutingStatusSummary: context => {
    // Check if alertSummary of the selected ASN exists. If it exists, do not need to fetch again.
    const accountName = context.getters.selectedAccountName;
    const memberId = context.getters.selectedAccountId;

    context.commit('setLoadingData', true);

    requestInstance
      .get(`/alerts/routing-status/summary/${accountName}`)
      .then(res => {
        context.commit('setLoadingData', false);

        if (res.data.alerts) {
          res.data.alerts = res.data.alerts.map(item => ({
            ...item,
            historyData: null,
            isShowDetail: false,
            snoozeTo: convertSnooze(item.snooze).snoozeTo
          }));
        }

        res.data &&
          context.commit('setAlertsRoutingStatusSummary', {
            memberId,
            data: res.data
          });
      })
      .catch(err => {
        console.error('fetchAlertsRoutingStatusSummary error: ', err);
        context.dispatch('setErrorInfoBanner', '', { root: true });
        context.commit('setLoadingData', false);
      });
  },

  fetchFiringAlertsRoutingStatusAmount: (
    context,
    { accountName, memberId }
  ) => {
    if (!accountName) accountName = context.getters.selectedAccountName;
    if (!memberId) memberId = context.getters.selectedAccountId;

    if (!accountName || !memberId) return;

    context.commit('setLoadingData', true);
    requestInstance
      .get(`/alerts/routing-status/firing/${accountName}`)
      .then(res => {
        context.commit('setLoadingData', false);
        context.commit('setFiringAlertsRoutingStatusAmount', {
          data: res.data,
          memberId: memberId
        });
      })
      .catch(err => {
        console.error('fetchFiringAlertsRoutingStatusAmount error: ', err);
        context.dispatch('setErrorInfoBanner', '', { root: true });
        context.commit('setLoadingData', false);
      });
  },

  createAlert: (context, payload) => {
    payload.snooze = null;

    context.commit('setSendingData', true);
    requestInstance
      .post('/alerts/routing-status', payload)
      .then(res => {
        context.commit('setSendingData', false);
        context.commit('setIsShowSuccessInfoBanner', true);
        context.commit('setSuccessInfoBannerText', 'Alert created');

        // Delete the current data of selected account from state.alertsRoutingStatusSummary as the data needs to be updated after adding an alert. Without doing it, they won't be updated correctly.
        delete context.state.alertsRoutingStatusSummary[
          context.getters.selectedAccountId
        ];
        context.dispatch('fetchAlertsRoutingStatusSummary');
        context.dispatch('fetchFiringAlertsRoutingStatusAmount', {});
      })
      .catch(err => {
        console.error('createAlert error: ', err);
        context.dispatch('setErrorInfoBanner', '', { root: true });
        context.commit('setSendingData', false);
      });
  },

  updateAlert: (context, payload) => {
    const { id, snooze } = context.state.editingAlertObj;
    const memberId = parseInt(context.getters.selectedAccountId);
    const data = {
      id,
      snooze,
      memberId,
      ...payload
    };

    context.commit('setEditingData', true);
    requestInstance
      .put('/alerts/routing-status', data)
      .then(res => {
        context.commit('setEditingData', false);
        context.commit('setEditingAlertObj', {});
        context.commit('setIsEditingAlert', false);
        context.commit('setIsShowSuccessInfoBanner', true);
        context.commit('setSuccessInfoBannerText', 'Alert updated');

        // Delete the current data of selected account from state.alertsRoutingStatusSummary as the data needs to be updated after updating an alert. Without doing it, they won't be updated correctly.
        delete context.state.alertsRoutingStatusSummary[
          context.getters.selectedAccountId
        ];
        context.dispatch('fetchAlertsRoutingStatusSummary');
        context.dispatch('fetchFiringAlertsRoutingStatusAmount', {});
      })
      .catch(err => {
        console.error('updateAlert error: ', err);
        context.dispatch('setErrorInfoBanner', '', { root: true });
        context.commit('setEditingData', false);
        context.commit('setEditingAlertObj', {});
        context.commit('setIsEditingAlert', false);
      });
  },

  snoozeAlert: (context, payload) => {
    const {
      id,
      filterTrigger,
      alertOnResolve,
      action,
      name
    } = context.state.editingAlertObj;
    const memberId = parseInt(context.getters.selectedAccountId);
    const data = {
      id,
      memberId,
      filterTrigger,
      alertOnResolve,
      action,
      name,
      snooze: [{ Included: payload.snoozeFrom }, { Included: payload.snoozeTo }]
    };

    context.commit('setSnoozingData', true);
    requestInstance
      .put('/alerts/routing-status', data)
      .then(res => {
        context.commit('setSnoozingData', false);
        context.commit('setEditingAlertObj', {});
        context.commit('setIsShowSuccessInfoBanner', true);
        context.commit('setSuccessInfoBannerText', 'Alert snoozed');

        // Delete the current data of selected account from state.alertsRoutingStatusSummary as the data needs to be updated after snoozing an alert. Without doing it, they won't be updated correctly.
        delete context.state.alertsRoutingStatusSummary[
          context.getters.selectedAccountId
        ];
        delete context.state.firingAlertsRoutingStatusAmount[
          context.getters.selectedAccountId
        ];

        context.dispatch('fetchAlertsRoutingStatusSummary');
        context.dispatch('fetchFiringAlertsRoutingStatusAmount', {});
      })
      .catch(err => {
        console.error('snoozeAlert error: ', err);
        context.dispatch('setErrorInfoBanner', '', { root: true });
        context.commit('setSnoozingData', false);
        context.commit('setEditingAlertObj', {});
      });
  },

  unSnoozeAlert: context => {
    const {
      id,
      filterTrigger,
      alertOnResolve,
      action,
      name
    } = context.state.editingAlertObj;
    const memberId = parseInt(context.getters.selectedAccountId);
    const data = {
      id,
      memberId,
      filterTrigger,
      alertOnResolve,
      action,
      name,
      snooze: null
    };

    context.commit('setUnSnoozingData', true);
    requestInstance
      .put('/alerts/routing-status', data)
      .then(res => {
        context.commit('setUnSnoozingData', false);
        context.commit('setEditingAlertObj', {});
        context.commit('setIsShowSuccessInfoBanner', true);
        context.commit('setSuccessInfoBannerText', 'Alert un-snoozed');

        // Delete the current data of selected account from state.alertsRoutingStatusSummary as the data needs to be updated after un-snoozing an alert. Without doing it, they won't be updated correctly.
        delete context.state.alertsRoutingStatusSummary[
          context.getters.selectedAccountId
        ];
        delete context.state.firingAlertsRoutingStatusAmount[
          context.getters.selectedAccountId
        ];

        context.dispatch('fetchAlertsRoutingStatusSummary');
        context.dispatch('fetchFiringAlertsRoutingStatusAmount', {});
      })
      .catch(err => {
        console.error('unSnoozeAlert error: ', err);
        context.dispatch('setErrorInfoBanner', '', { root: true });
        context.commit('setUnSnoozingData', false);
        context.commit('setEditingAlertObj', {});
      });
  },

  deleteAlert: (context, payload) => {
    context.commit('setDeletingData', true);

    requestInstance
      .delete(`/alerts/routing-status/${payload}`)
      .then(res => {
        context.commit('setDeletingData', false);
        context.commit('setEditingAlertObj', {});
        context.commit('setIsShowSuccessInfoBanner', true);
        context.commit('setSuccessInfoBannerText', 'Alert deleted');

        // Delete the current data of selected account from state.alertsRoutingStatusSummary as the data needs to be updated after deleting an alert. Without doing it, they won't be updated correctly.
        delete context.state.alertsRoutingStatusSummary[
          context.getters.selectedAccountId
        ];
        delete context.state.firingAlertsRoutingStatusAmount[
          context.getters.selectedAccountId
        ];

        context.dispatch('fetchAlertsRoutingStatusSummary');
        context.dispatch('fetchFiringAlertsRoutingStatusAmount', {});
      })
      .catch(err => {
        console.error('deleteAlert error: ', err);
        context.commit('setDeletingData', false);
        context.commit('setEditingAlertObj', {});
      });
  },

  fetchHistoryOfSelectedAlert: (context, alertId) => {
    const alertObj =
      context.getters.alertsSummaryOfSelectedAccount.alerts &&
      context.getters.alertsSummaryOfSelectedAccount.alerts.find(
        alert => alert.id === alertId
      );

    // Check whether the history of selected alert has been cached
    if (alertObj && alertObj.historyData) return;

    const end = dayjs().toISOString();
    const start = dayjs(START_DATE_OF_ALERTS_ROUTING_STATUS).toISOString();

    // Fetch the history of selected alert
    context.commit('setLoadingData', true);
    requestInstance
      .get(`/alerts/routing-status/history/${alertId}`, {
        params: {
          start,
          end
        }
      })
      .then(res => {
        setTimeout(() => {
          context.commit('setLoadingData', false);
        }, 300);

        if (res.data) {
          const formattedData = res.data.map(item => {
            let filter = '';
            let trigger = '';
            let reason = item.reason;
            if (item.triggerOrigin && item.triggerOrigin.length) {
              filter = 'Origin AS';
              trigger = item.triggerOrigin.map(i => `AS${i}`).join(', ');
            } else if (item.triggerPrefix && item.triggerPrefix.length) {
              filter = 'Prefix';
              trigger = item.triggerPrefix.join(', ');
            }

            const {
              isTriggerSliced,
              slicedTrigger,
              moreTriggerAmount
            } = sliceTrigger(trigger);
            const { isReasonSliced, slicedReason } = sliceReason(reason);

            return {
              ...item,
              filter,
              trigger,
              reason,
              isTriggerSliced,
              slicedTrigger,
              moreTriggerAmount,
              isReasonSliced,
              slicedReason
            };
          });

          const selectedAccountId = context.getters.selectedAccountId;
          context.commit('insertHistoryDataIntoSelectedAlert', {
            selectedAccountId,
            alertId,
            data: formattedData
          });
        }
      })
      .catch(err => {
        console.error('fetchHistoryOfSelectedAlert error:', err);
        context.dispatch('setErrorInfoBanner', '', { root: true });
        context.commit('setLoadingData', false);
      });
  }
};

const mutations = {
  setLoadingData: (state, boolean) => {
    state.isLoadingData = boolean;
  },

  setSendingData: (state, boolean) => {
    state.isSendingData = boolean;
  },

  setDeletingData: (state, boolean) => {
    state.isDeletingData = boolean;
  },

  setEditingData: (state, boolean) => {
    state.isEditingData = boolean;
  },

  setSnoozingData: (state, boolean) => {
    state.isSnoozingData = boolean;
  },

  setUnSnoozingData: (state, boolean) => {
    state.isUnSnoozingData = boolean;
  },

  setIsShowCreateAlertModal: (state, boolean) => {
    state.isShowCreateAlertModal = boolean;
  },

  setEditingAlertObj: (state, editingAlertObj) => {
    state.editingAlertObj = editingAlertObj;
  },

  setIsEditingAlert: (state, boolean) => {
    state.isEditingAlert = boolean;
  },

  setIsShowSnoozeAlertModal: (state, boolean) => {
    state.isShowSnoozeAlertModal = boolean;
  },

  setIsShowDeleteAlertModal: (state, boolean) => {
    state.isShowDeleteAlertModal = boolean;
  },

  setAlertNameQuery: (state, val) => {
    state.alertNameQuery = val;
  },

  setSearchQuery: (state, val) => {
    state.searchQuery = val;
  },

  setIsShowDetailOnMobile: (
    state,
    { selectedAccountId, alertId, isShowDetail }
  ) => {
    const alert =
      state.alertsRoutingStatusSummary[selectedAccountId] &&
      state.alertsRoutingStatusSummary[selectedAccountId].alerts &&
      state.alertsRoutingStatusSummary[selectedAccountId].alerts.find(
        alert => alert.id === alertId
      );

    alert && (alert.isShowDetail = isShowDetail);
  },

  setIsShowSuccessInfoBanner: (state, boolean) => {
    state.isShowSuccessInfoBanner = boolean;
  },

  setSuccessInfoBannerText: (state, msg) => {
    state.successInfoBannerText = msg;
  },

  setErrorInfoBanner: (context, err) => {
    if (err.response && err.response.data && err.response.data.message) {
      context.commit('setErrorInfoBannerText', err.response.data.message);
      context.commit('setIsShowErrorInfoBanner', true);
    } else if (err.message) {
      context.commit('setErrorInfoBannerText', err.message);
      context.commit('setIsShowErrorInfoBanner', true);
    } else {
      context.commit(
        'setErrorInfoBannerText',
        'There was a problem processing your request and we are investigating. Please try again later.'
      );
      context.commit('setIsShowErrorInfoBanner', true);
    }
  },

  setIsShowErrorInfoBanner: (state, boolean) => {
    state.isShowErrorInfoBanner = boolean;
  },

  setErrorInfoBannerText: (state, msg) => {
    state.errorInfoBannerText = msg;
  },

  setDelegatedPrefixes: (state, payload) => {
    const { prefixes, memberId } = payload;
    state.delegatedPrefixes = {
      ...state.delegatedPrefixes,
      [memberId]: prefixes
    };
  },

  setMemberContacts: (state, payload) => {
    state.memberContacts = {
      ...state.memberContacts,
      [payload.memberId]: payload.memberContacts
    };
  },

  setAlertsRoutingStatusSummary: (state, payload) => {
    // Sort alerts list in descending order by statusOrder, i.e. 'Firing', 'Snoozed', 'Fired', 'Clean', then sort by name with same statusOrder
    payload.data.alerts = payload.data.alerts.map(item => ({
      ...item,
      statusOrder: setStatusOrder(item.status)
    }));
    payload.data.alerts.sort((a, b) => {
      if (b.statusOrder > a.statusOrder) return 1;
      if (b.statusOrder < a.statusOrder) return -1;
      if (b.statusOrder === a.statusOrder) {
        if ((b.name || '').toUpperCase() > (a.name || '').toUpperCase())
          return -1;
        if ((b.name || '').toUpperCase() < (a.name || '').toUpperCase())
          return 1;
        return 0;
      }
    });

    state.alertsRoutingStatusSummary = {
      ...state.alertsRoutingStatusSummary,
      [payload.memberId]: payload.data
    };
  },

  setFiringAlertsRoutingStatusAmount: (state, payload) => {
    const { data, memberId } = payload;
    state.firingAlertsRoutingStatusAmount = {
      ...state.firingAlertsRoutingStatusAmount,
      [memberId]: data
    };
  },

  insertHistoryDataIntoSelectedAlert: (
    state,
    { selectedAccountId, alertId, data }
  ) => {
    const alert =
      state.alertsRoutingStatusSummary[selectedAccountId] &&
      state.alertsRoutingStatusSummary[selectedAccountId].alerts &&
      state.alertsRoutingStatusSummary[selectedAccountId].alerts.find(
        alert => alert.id === alertId
      );

    // Sort history data in timestamp descending order, i.e. from latest to oldest
    data.sort(
      (a, b) => dayjs(b.timestamp).valueOf() - dayjs(a.timestamp).valueOf()
    );

    alert && (alert.historyData = data);
  },

  setShowMoreModalObj: (state, payload) => {
    state.showMoreModalObj = { ...state.showMoreModalObj, ...payload };
  },

  setExpandedAlertId: (state, val) => {
    state.expandedAlertId = val;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};

function setStatusOrder(alertStatus) {
  let statusOrder = 0;

  switch (alertStatus) {
    case STATUS.CLEAN:
      statusOrder = 0;
      break;
    case STATUS.FIRED:
      statusOrder = 1;
      break;
    case STATUS.SNOOZED:
      statusOrder = 2;
      break;
    case STATUS.FIRING:
      statusOrder = 3;
      break;
    default:
      break;
  }

  return statusOrder;
}

function convertSnooze(snooze) {
  if (!snooze || snooze.length !== 2)
    return {
      snoozeTo: null,
      snoozeFrom: null
    };

  if (!snooze[0].Included || !snooze[1].Included)
    return {
      snoozeTo: null,
      snoozeFrom: null
    };

  if (
    dayjs(snooze[0].Included).valueOf() > dayjs(snooze[1].Included).valueOf()
  ) {
    return {
      snoozeTo: snooze[0].Included,
      snoozeFrom: snooze[1].Included
    };
  } else {
    return {
      snoozeTo: snooze[1].Included,
      snoozeFrom: snooze[0].Included
    };
  }
}

function sliceTrigger(trigger) {
  if (!trigger || typeof trigger !== 'string')
    return { isTriggerSliced: false, slicedTrigger: '', moreTriggerAmount: 0 };

  if (trigger.split(', ').length > MAX_TRIGGERS_SHOWN) {
    return {
      isTriggerSliced: true,
      slicedTrigger: trigger
        .split(', ')
        .slice(0, 10)
        .join(', '),
      moreTriggerAmount: trigger.split(', ').length - MAX_TRIGGERS_SHOWN
    };
  } else {
    return {
      isTriggerSliced: false,
      slicedTrigger: trigger,
      moreTriggerAmount: 0
    };
  }
}

function sliceReason(reason) {
  if (!reason || typeof reason !== 'string')
    return { isReasonSliced: false, slicedReason: '' };

  if (reason.length > MAX_INCIDENT_CHARACTERS_SHOWN) {
    let slicedReason = reason.slice(0, MAX_INCIDENT_CHARACTERS_SHOWN);
    const lastIndexOfComma = slicedReason.lastIndexOf(',');
    slicedReason = slicedReason.slice(0, lastIndexOfComma);

    return {
      isReasonSliced: true,
      slicedReason: slicedReason
    };
  } else {
    return {
      isReasonSliced: false,
      slicedReason: reason
    };
  }
}
